import { Box, Button, Grid, Skeleton, Typography } from '@mui/material';
import * as React from 'react';
import { useDispatch, useSelector } from 'react-redux';

import {
  currentGalleryCarIdSet,
  getAllGalleryImagesLow,
} from '../../store/gallery-slice';

import {
  activeTabSet,
  dragToExploreVisibilitySet,
  galleryTabSet,
  getCarImageRatio,
  rotate360VisibilitySet,
} from '../../store/car-slice';

import { carStatusSet, interiorStatusSet } from '../../store/car-slice';
import { useWindowSize } from '../../hooks/use-window-size';
import useListImageLoader from '../../hooks/use-list-image-loader';
import { useInteriorExist } from '../../hooks/use-interior-exist';

export default function VdpImageList() {
  // Selectors
  const { interiorGalleryLow, exteriorGalleryLow } = useSelector(
    getAllGalleryImagesLow,
  );

  // States
  const [src1, setSrc1]: any = React.useState({
    id: '',
    url: '',
    loading: true,
  });
  const [src2, setSrc2] = React.useState({
    id: '',
    url: '',
    loading: true,
  });
  const [src3, setSrc3] = React.useState({
    id: '',
    url: '',
    loading: true,
  });
  const [src4, setSrc4] = React.useState({
    id: '',
    url: '',
    loading: true,
  });

  // Custom

  let first = interiorGalleryLow[0];
  if (interiorGalleryLow.length === 0) {
    first = exteriorGalleryLow[3];
  }
  const second = exteriorGalleryLow[0];
  const third = exteriorGalleryLow[1];
  const fourth = exteriorGalleryLow[2];
  const dispatch = useDispatch();
  const { width, height } = useWindowSize();

  const allGalleryLow = React.useMemo(
    () => [...exteriorGalleryLow, ...interiorGalleryLow],
    [interiorGalleryLow, exteriorGalleryLow],
  );

  const aspectRatio = useSelector(getCarImageRatio);

  const handleFullscreen = () => {
    dispatch(carStatusSet('CAR'));
    dispatch(dragToExploreVisibilitySet(false));
    dispatch(rotate360VisibilitySet(false));
  };

  const openCenterConsole = () => {
    dispatch(activeTabSet('interior'));
    dispatch(interiorStatusSet('center-console'));
    handleFullscreen();
  };

  const openBackSeat = () => {
    dispatch(activeTabSet('interior'));
    dispatch(interiorStatusSet('back-seat'));
    handleFullscreen();
  };

  const openGallery = () => {
    dispatch(activeTabSet('gallery'));

    //if interior 360 is not exist navigate to first interior image gallery
    if (interiorGalleryLow.length > 0) {
      dispatch(currentGalleryCarIdSet(interiorGalleryLow[0]?.id));
      dispatch(galleryTabSet('gallery-interior'));
    } else if (exteriorGalleryLow.length > 0) {
      //if interior gallery is not exist navigate to first exterior image gallery
      dispatch(currentGalleryCarIdSet(exteriorGalleryLow[0]?.id));
      dispatch(galleryTabSet('gallery-exterior'));
    }
    handleFullscreen();
  };

  const handleClickCarGallery = (id: string) => {
    dispatch(activeTabSet('gallery'));
    dispatch(currentGalleryCarIdSet(id));
    handleFullscreen();
  };

  const allGalleryLength = React.useMemo(
    () => allGalleryLow?.length,
    [allGalleryLow],
  );

  const { isInteriorExist, isCenterConsoleExist, isBackSeatExist } =
    useInteriorExist();

  useListImageLoader(first, setSrc1);
  useListImageLoader(second, setSrc2);
  useListImageLoader(third, setSrc3);
  useListImageLoader(fourth, setSrc4);

  const handleClickCarInterior = () => {
    if (isCenterConsoleExist) {
      openCenterConsole();
    } else if (isBackSeatExist) {
      openBackSeat();
    } else {
      openGallery();
    }
  };

  type ListType = {
    img: {
      id: string;
      url: string;
      loading: boolean;
    };
  };

  const webList: ListType[] = [
    {
      img: src1,
    },
    {
      img: src2,
    },
    {
      img: src3,
    },
    {
      img: src4,
    },
  ];

  const isWiderThanScreen = width / height > aspectRatio;

  if (allGalleryLength >= 4) {
    const vdpImageList = webList;
    return (
      <Grid
        gap={1}
        gridTemplateColumns={'1fr'}
        gridTemplateRows={'1fr 1fr 1fr 1fr'}
        sx={{
          overflow: 'auto',
          display: 'grid',
          ...(isWiderThanScreen ? { height: '100%' } : { width: '100%' }),
          gridTemplateColumns: isWiderThanScreen
            ? '1fr'
            : `repeat(${vdpImageList.length}, 1fr)`,
          gridTemplateRows: isWiderThanScreen
            ? `repeat(${vdpImageList.length}, 1fr)`
            : '1fr',
        }}
      >
        {vdpImageList?.map((item, index) => (
          <Grid
            item
            key={index}
            sx={{
              overflow: 'auto',
              maxHeight: '100%',
              flex: '1',
              display: 'flex',
              flexDirection: width / height > aspectRatio ? 'column' : 'row',
              justifyContent:
                index === 0
                  ? 'flex-start'
                  : index === vdpImageList.length - 1
                    ? 'flex-end'
                    : 'center',
            }}
          >
            <Button
              className="animate-fade"
              onClick={() =>
                index === 0
                  ? handleClickCarInterior()
                  : handleClickCarGallery(item?.img?.id)
              }
              style={{
                objectFit: 'cover',
                ...(isWiderThanScreen ? { height: '100%' } : { width: '100%' }),
                borderRadius: 10,
                padding: 0,
                display: 'flex',
                alignItems:
                  index === 0
                    ? 'flex-start'
                    : index === vdpImageList.length - 1
                      ? 'flex-end'
                      : 'center',
              }}
            >
              {item.img.loading && (
                <Skeleton
                  height={'100%'}
                  style={{
                    maxHeight: '100%',
                    maxWidth: '100%',
                    aspectRatio: aspectRatio,
                  }}
                  width={'100%'}
                />
              )}
              <img
                onLoad={() => {
                  vdpImageList[index].img.loading = false;
                }}
                src={item?.img?.url}
                style={{
                  objectFit: 'cover',
                  visibility: item.img.loading ? 'hidden' : 'visible',
                  height: '100%',
                  width: item.img.loading ? 0 : '100%',
                  maxWidth: '100%',
                  maxHeight: '100%',
                  aspectRatio: aspectRatio,
                  borderRadius: 10,
                  filter:
                    index === vdpImageList?.length - 1
                      ? 'brightness(0.25)'
                      : '',
                }}
                title="image"
              />
              {index === vdpImageList?.length - 1 && item?.img?.url !== '' && (
                <Box
                  sx={{
                    left: '50%',
                    margin: 'auto',
                    position: 'absolute',
                    textAlign: 'center',
                    top: '50%',
                    transform: 'translate(-50%, -50%)',
                  }}
                >
                  <Typography
                    color={'white'}
                    fontWeight={'700'}
                    gutterBottom
                    sx={{
                      fontSize: {
                        xs: 25,
                        sm: 40,
                        md: 25,
                        lg: 30,
                        xl: 30,
                      },
                      m: 0,
                    }}
                  >
                    +{allGalleryLength}
                  </Typography>
                </Box>
              )}
            </Button>
          </Grid>
        ))}
      </Grid>
    );
  }
}
